/**
 * BookletPDF — Sprint 2
 * A sober, printable keepsake of a sealed journey: figures, personal records and
 * the loose threads it left open. No photos, no arcade "achievements" — discrete
 * stats only. Receives pre-built data + labels (no i18n context inside react-pdf).
 *
 * Props: { booklet } where booklet = {
 *   title, subtitle,
 *   headings: { figures, records, threads },
 *   figures: [{ value, label }],
 *   records: [{ label, value, sub? }],
 *   threads: [string],
 * }
 */
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

const colors = {
  ink: '#3d3028',
  brown: '#8B7355',
  terracotta: '#C4714A',
  blue: '#2D5A6E',
  line: '#e8e1d6',
  cream: '#f5f1eb',
};

const s = StyleSheet.create({
  page: { backgroundColor: '#ffffff', padding: 48, fontFamily: 'Helvetica' },
  title: { fontSize: 28, color: colors.ink, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  subtitle: { fontSize: 12, color: colors.brown, marginBottom: 28 },
  heading: { fontSize: 10, color: colors.blue, fontFamily: 'Helvetica-Bold', letterSpacing: 1.5, textTransform: 'uppercase', marginTop: 22, marginBottom: 12 },
  figGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  fig: { width: '33.33%', marginBottom: 16 },
  figValue: { fontSize: 22, color: colors.blue, fontFamily: 'Helvetica-Bold' },
  figLabel: { fontSize: 8, color: colors.brown, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 },
  recRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.line },
  recLabel: { fontSize: 11, color: colors.ink, fontFamily: 'Helvetica-Bold' },
  recValue: { fontSize: 11, color: colors.terracotta, fontFamily: 'Helvetica-Bold' },
  recSub: { fontSize: 9, color: colors.brown, marginTop: 1, textAlign: 'right' },
  thread: { flexDirection: 'row', paddingVertical: 5 },
  threadDot: { fontSize: 11, color: colors.terracotta, marginRight: 8 },
  threadText: { fontSize: 11, color: colors.ink, flex: 1 },
  footer: { position: 'absolute', bottom: 28, left: 48, right: 48, fontSize: 8, color: colors.brown, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1 },
});

export default function BookletPDF({ booklet }) {
  const { title, subtitle, headings = {}, figures = [], records = [], threads = [] } = booklet || {};

  return (
    <Document>
      <Page size="A5" style={s.page}>
        <Text style={s.title}>{title}</Text>
        {subtitle ? <Text style={s.subtitle}>{subtitle}</Text> : null}

        {figures.length > 0 && (
          <>
            <Text style={s.heading}>{headings.figures}</Text>
            <View style={s.figGrid}>
              {figures.map((f, i) => (
                <View key={i} style={s.fig}>
                  <Text style={s.figValue}>{f.value}</Text>
                  <Text style={s.figLabel}>{f.label}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {records.length > 0 && (
          <>
            <Text style={s.heading}>{headings.records}</Text>
            {records.map((r, i) => (
              <View key={i} style={s.recRow}>
                <Text style={s.recLabel}>{r.label}</Text>
                <View>
                  <Text style={s.recValue}>{r.value}</Text>
                  {r.sub ? <Text style={s.recSub}>{r.sub}</Text> : null}
                </View>
              </View>
            ))}
          </>
        )}

        {threads.length > 0 && (
          <>
            <Text style={s.heading}>{headings.threads}</Text>
            {threads.map((th, i) => (
              <View key={i} style={s.thread}>
                <Text style={s.threadDot}>—</Text>
                <Text style={s.threadText}>{th}</Text>
              </View>
            ))}
          </>
        )}

        <Text style={s.footer}>WAYFARE · LOS DÍAS</Text>
      </Page>
    </Document>
  );
}
